import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Avatar,
  Stack
} from "@mui/material";
import { 
  ChevronLeft, 
  ChevronRight, 
  Print, 
  Download, 
  DragIndicator,
  Reorder,
  Phone,
  Email,
  LocationOn,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: true,
  columns: 1,
  colorOptions: [
    { value: '#4b2c4d', label: 'Purple' },
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#f44336', label: 'Red' }
  ]
};

const defaultData = {
  firstName: "WAHANA",
  lastName: "PRIMA",
  currentPosition: "SPECIALIST GRAPHIC DESIGNER",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "Any City",
  website: "https://linkedin.com/in/wahana-prima",
  profile: "Highly creative and detail-oriented Graphic Designer with over 6 years of experience in visual communication.",
  skills: [
    { name: "Web Design", rating: 95 },
    { name: "Design Thinking", rating: 90 },
    { name: "Wireframe Creation", rating: 85 },
    { name: "Problem-Solving", rating: 80 },
    { name: "Computer Literacy", rating: 75 },
    { name: "Front End Coding", rating: 70 },
  ],
  education: [
    {
      degree: "BACHELOR OF DEVELOPER",
      fieldOfStudy: "",
      schoolName: "BORCELLE UNIVERSITY",
      schoolLocation: "",
      gradMonth: "May",
      gradYear: "2014",
      description: "Graduated with honors."
    },
    {
      degree: "MASTER OF DESIGNER",
      fieldOfStudy: "",
      schoolName: "BORCELLE UNIVERSITY",
      schoolLocation: "",
      gradMonth: "May",
      gradYear: "2016",
      description: "Specialized in digital media."
    }
  ],
  workExperiences: [
    {
      jobTitle: "GRAPHIC SOFTWARE DESIGNER",
      employer: "WARDIERE COMPANY",
      location: "",
      startMonth: "Jan",
      startYear: "2014",
      endMonth: "Dec",
      endYear: "2018",
      current: false,
      description: "Led the creative direction for multiple high-profile clients. Managed a team of 3 junior designers."
    },
    {
      jobTitle: "WEB CONTENT MANAGER",
      employer: "LARANA COMPANY",
      location: "",
      startMonth: "Jan",
      startYear: "2018",
      endMonth: "Present",
      endYear: "",
      current: true,
      description: "Managing all digital assets and visual content for the company's online presence."
    }
  ],
  certifications: [],
  photoUrl: "https://via.placeholder.com/150"
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultSectionOrder = [
  'summary',
  'skills', 
  'experience',
  'education',
  'certifications',
  'languages',
  'accomplishments',
  'additionalInfo',
  'softwareSkills',
  'volunteering',
  'interests',
  'references'
];

const Resume34 = ({
  theme = "#2d3436",
  color = "#2d3436",
  font = "Inter, Roboto, sans-serif",
  fontSize = "13px",
  fontStyle = "normal",
  headingSize = "18px",
  sectionSpacing = 30,
  paragraphSpacing = 12,
  lineSpacing = 22,
  topBottomMargin = 15,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };
  
  const [themeColor, setThemeColor] = useState(color || theme);
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const resumeRef = useRef(null);

  const [savedSummary, setSavedSummary] = useState(data.profile);
  const [savedSkills, setSavedSkills] = useState(data.skills);
  const [educationEntries, setEducationEntries] = useState(data.education);
  const [workExperiences, setWorkExperiences] = useState(data.workExperiences);
  const [savedCertifications, setSavedCertifications] = useState(data.certifications);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedSoftware, setSavedSoftware] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [photoUrl, setPhotoUrl] = useState(data.photoUrl);
  const [imageLoadError, setImageLoadError] = useState(false);

  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  useEffect(() => {
    setThemeColor(color || theme);
    
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }
    
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      setEducationEntries(JSON.parse(storedEntries));
    }
    
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }
    
    const storedWorkExperiences = JSON.parse(localStorage.getItem("workExperiences")) || [];
    setWorkExperiences(storedWorkExperiences);
    
    const websites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(websites);
    
    const certifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(certifications);
    
    const languages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(languages);
    
    const accomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(accomplishments);
    
    const additionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(additionalInfo);
    
    const software = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSavedSoftware(software);
    
    const references = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(references);
    
    const volunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(volunteering);
    
    const interests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(interests);
    
    const storedPhoto = localStorage.getItem("photoUrl");
    if (storedPhoto && storedPhoto.trim() !== "") {
      setPhotoUrl(storedPhoto);
      setImageLoadError(false);
    } else {
      setPhotoUrl(data.photoUrl);
    }

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    const savedSectionOrder = localStorage.getItem("resume34SectionOrder");
    if (savedSectionOrder) {
      const parsedOrder = JSON.parse(savedSectionOrder);
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allSections = [...filteredOrder, ...currentCustomSectionIds];
      setSectionOrder(allSections);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedSectionOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
      setSectionOrder(updatedSectionOrder);
    }
  }, [color, theme]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentOrder = [...sectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      const newOrder = [...filteredOrder, ...currentCustomSectionIds];
      
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume34SectionOrder", JSON.stringify(newOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentOrder = [...sectionOrder];
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      
      if (filteredOrder.length !== currentOrder.length) {
        setSectionOrder(filteredOrder);
        localStorage.setItem("resume34SectionOrder", JSON.stringify(filteredOrder));
      }
    }
  }, [customSections]);

  // Update photo URL when formData changes
  useEffect(() => {
    if (formData && formData.photo) {
      setPhotoUrl(formData.photo);
      setImageLoadError(false);
    } else if (formData && formData.photoUrl) {
      setPhotoUrl(formData.photoUrl);
      setImageLoadError(false);
    }
  }, [formData?.photo, formData?.photoUrl]);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume34SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const getPageWidth = () => page.width;
  const getPageHeight = () => page.height;

  // Section Header with Dynamic Background Color
  const SectionHeader = ({ title }) => (
    <Box style={{
      backgroundColor: themeColor,
      paddingTop: "8px",
      paddingBottom: "8px",
      paddingLeft: "8px",
      paddingRight: "8px",
      marginBottom: "12px",
      textAlign: "center",
      borderRadius: "4px",
      borderBottom: `${lineWeight}px solid ${themeColor}`,
    }}>
      <Typography component="div" style={{
        fontWeight: "bold",
        letterSpacing: 2,
        color: "#fff",
        fontSize: headingSize,
        textTransform: "uppercase",
        fontFamily: font,
        fontStyle: fontStyle,
      }}>
        {title}
      </Typography>
    </Box>
  );

  const paragraphStyle = {
    marginTop: `${paragraphSpacing}px`,
    textIndent: `${paragraphIndent}px`,
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      index,
      sectionId: sectionOrder[index]
    }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = dropData.index;

    if (sourceIndex !== targetIndex) {
      const newSectionOrder = [...sectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setSectionOrder(newSectionOrder);
    }

    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => {
    const customSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
    const newDefaultOrder = [...defaultSectionOrder, ...customSectionIds];
    setSectionOrder(newDefaultOrder);
  };

  const handleArrangeSectionsClick = () => {
    setArrangeDialogOpen(true);
  };

  const handleArrangeDialogClose = () => {
    setArrangeDialogOpen(false);
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'summary': 'About Me',
      'skills': 'Skills',
      'experience': 'Work Experience',
      'education': 'Education',
      'certifications': 'Certifications',
      'languages': 'Languages',
      'accomplishments': 'Accomplishments',
      'additionalInfo': 'Additional Information',
      'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering',
      'interests': 'Interests',
      'references': 'References'
    };
    
    if (sectionId.startsWith('custom_')) {
      const customName = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return customName.charAt(0).toUpperCase() + customName.slice(1);
    }
    
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = () => {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIndicator sx={{ mr: 1 }} />
          Resume Sections
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            ({sectionOrder.length} sections)
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Drag and drop to reorder sections.
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          {sectionOrder.length > 0 ? (
            sectionOrder.map((sectionId, index) => (
              <Paper
                key={sectionId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  p: 2,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
                  boxShadow: dragOverIndex === index ? 2 : 1,
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  transition: 'all 0.2s ease',
                }}
              >
                <DragIndicator color="action" />
                <Typography sx={{ ml: 1, flexGrow: 1 }}>
                  {getSectionTitle(sectionId)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {index + 1}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
              No sections available.
            </Typography>
          )}
        </Box>
        <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
          Reset Section Order
        </Button>
      </Box>
    );
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'summary':
        return (
          <Box sx={{ mt: `${sectionSpacing}px` }} className="resume-section" id="summary-section">
            <SectionHeader title="About Me" />
            <Typography variant="body2" sx={{
              ...paragraphStyle,
              textAlign: "center",
              px: 2,
              mb: 2,
              lineHeight: 1.6,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}>
              {savedSummary}
            </Typography>
          </Box>
        );
      
      case 'skills':
        return savedSkills.length > 0 ? (
          <Box className="resume-section" id="skills-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Skills" />
            <Grid container spacing={2} sx={{ textAlign: "left", px: 1 }}>
              {savedSkills.length <= 3 ? (
                <Grid item xs={12}>
                  <ul style={{ margin: 0, paddingLeft: "15px", fontSize }}>
                    {savedSkills.map((skill, index) => (
                      <li key={index} style={{ marginBottom: "6px", wordBreak: "break-word" }}>{skill.name}</li>
                    ))}
                  </ul>
                </Grid>
              ) : (
                [
                  savedSkills.slice(0, Math.ceil(savedSkills.length / 3)),
                  savedSkills.slice(Math.ceil(savedSkills.length / 3), Math.ceil(savedSkills.length / 3) * 2),
                  savedSkills.slice(Math.ceil(savedSkills.length / 3) * 2)
                ].map((skillGroup, idx) => (
                  <Grid item xs={4} key={idx}>
                    <ul style={{ margin: 0, paddingLeft: "15px", fontSize }}>
                      {skillGroup.map(skill => (
                        <li key={skill.name} style={{ marginBottom: "6px", wordBreak: "break-word" }}>{skill.name}</li>
                      ))}
                    </ul>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        ) : null;
      
      case 'experience':
        return workExperiences.length > 0 ? (
          <Box className="resume-section" id="experience-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Work Experience" />
            <Box sx={{ mb: 2 }}>
              {workExperiences.map((work, index) => (
                <Grid container spacing={1} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <Typography sx={{ 
                      fontWeight: "bold", 
                      color: "gray", 
                      fontSize: fontSize, 
                      fontFamily: font,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>
                      {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography sx={{ 
                      fontWeight: "bold", 
                      fontSize: fontSize, 
                      fontFamily: font,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>
                      {work.employer} | {work.jobTitle}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: "12px", 
                      color: "#666", 
                      mt: 0.5, 
                      fontFamily: font,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>
                      {work.description}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        ) : null;
      
      case 'education':
        return educationEntries.length > 0 ? (
          <Box className="resume-section" id="education-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Education" />
            <Box sx={{ mb: 2 }}>
              {educationEntries.map((edu, index) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                
                return (
                  <Grid container spacing={1} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Typography sx={{ 
                        fontWeight: "bold", 
                        color: "gray", 
                        fontSize: fontSize, 
                        fontFamily: font,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {edu.gradYear || `${edu.startYear} - ${edu.endYear}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography sx={{ 
                        fontWeight: "bold", 
                        fontSize: fontSize, 
                        fontFamily: font,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {institutionName} | {degreeName}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: "12px", 
                        color: "#666", 
                        mt: 0.5, 
                        fontFamily: font,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {edu.description}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          </Box>
        ) : null;
      
      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box className="resume-section" id="certifications-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Certifications" />
            {savedCertifications.map((cert, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box className="resume-section" id="languages-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Languages" />
            {savedLanguages.map((lang, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                {lang.name} {lang.level ? `– ${lang.level}` : ""}
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box className="resume-section" id="accomplishments-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Accomplishments" />
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                • {acc}
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box className="resume-section" id="additional-info-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Additional Information" />
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                • {info}
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'softwareSkills':
        return savedSoftware.length > 0 ? (
          <Box className="resume-section" id="software-skills-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Software Skills" />
            {savedSoftware.map((software, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                {software.name} – {software.level}%
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box className="resume-section" id="volunteering-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Volunteering" />
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={getTextStyle({ wordBreak: "break-word" })}>
                  {vol.subtopic}
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: "italic", ...globalFontStyle }}>
                  {vol.fromDate} - {vol.toDate}
                </Typography>
                <Typography variant="body2" sx={{ ...paragraphStyle, wordBreak: "break-word" }}>
                  {vol.content}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null;
      
      case 'interests':
        return savedInterests.length > 0 ? (
          <Box className="resume-section" id="interests-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="Interests" />
            {savedInterests.map((interest, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                • {interest}
              </Typography>
            ))}
          </Box>
        ) : null;
      
      case 'references':
        return savedReferences.length > 0 ? (
          <Box className="resume-section" id="references-section" sx={{ mt: `${sectionSpacing}px` }}>
            <SectionHeader title="References" />
            {savedReferences.map((ref, index) => (
              <Typography key={index} variant="body2" sx={paragraphStyle}>
                • {ref}
              </Typography>
            ))}
          </Box>
        ) : null;
      
      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          const validItems = items.filter(item => item && item.trim() !== '');
          
          if (validItems.length > 0) {
            return (
              <Box className="resume-section" id={`custom-${customSectionName}-section`} sx={{ mt: `${sectionSpacing}px` }}>
                <SectionHeader title={customSectionName.replace(/_/g, ' ')} />
                {validItems.map((item, i) => (
                  <Typography key={i} variant="body2" sx={paragraphStyle}>
                    • {item}
                  </Typography>
                ))}
              </Box>
            );
          }
          return null;
        }
        return null;
    }
  };

  // Add global font style to HTML content
  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    const baseStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word;`;
    const spanStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;`;
    
    // For divs - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<div([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<div${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<div${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<div(?![^>]*style)([^>]*)>/g, `<div$1style="${baseStyles}">`);
    
    // For p tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<p([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<p${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<p${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<p(?![^>]*style)([^>]*)>/g, `<p$1style="${baseStyles}">`);
    
    // For span tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<span([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
        return `<span${before}style="${newStyle}"`;
      } else {
        const newStyle = `${spanStyles}${style}`;
        return `<span${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<span(?![^>]*style)([^>]*)>/g, `<span$1style="${spanStyles}">`);
    
    // For headings - preserve existing font-size if present
    const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
    headingTags.forEach(tag => {
      htmlContent = htmlContent.replace(new RegExp(`<${tag}([^>]*?)style="([^"]*)"`, 'g'), (match, before, style) => {
        if (style.includes('font-size:')) {
          const newStyle = `font-family: ${font}; font-style: ${fontStyle}; ${style}`;
          return `<${tag}${before}style="${newStyle}"`;
        } else {
          const newStyle = `font-family: ${font}; font-size: ${headingSize}; font-style: ${fontStyle}; ${style}`;
          return `<${tag}${before}style="${newStyle}"`;
        }
      });
      htmlContent = htmlContent.replace(new RegExp(`<${tag}(?![^>]*style)([^>]*)>`, 'g'), `<${tag}$1style="font-family: ${font}; font-size: ${headingSize}; font-style: ${fontStyle};">`);
    });
    
    // For list items
    htmlContent = htmlContent.replace(/<li([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; ${style}`;
        return `<li${before}style="${newStyle}"`;
      } else {
        const newStyle = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; ${style}`;
        return `<li${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<li(?![^>]*style)([^>]*)>/g, `<li$1style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word;">`);
    
    // For lists
    htmlContent = htmlContent.replace(/<ul([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      const newStyle = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
      return `<ul${before}style="${newStyle}"`;
    });
    htmlContent = htmlContent.replace(/<ul(?![^>]*style)([^>]*)>/g, `<ul$1style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;">`);
    
    htmlContent = htmlContent.replace(/<ol([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      const newStyle = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
      return `<ol${before}style="${newStyle}"`;
    });
    htmlContent = htmlContent.replace(/<ol(?![^>]*style)([^>]*)>/g, `<ol$1style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;">`);
    
    return htmlContent;
  };

  const splitContentIntoPages = useCallback(() => {
    if (!resumeRef.current) return;

    const sections = Array.from(resumeRef.current.querySelectorAll('.resume-section'));
    const headerHeight = resumeRef.current.querySelector('.resume-header')?.offsetHeight || 0;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const availableHeightFirstPage = pageHeightInPx - headerHeight - (2 * topBottomMargin);
    const availableHeightSubsequent = pageHeightInPx - (2 * topBottomMargin);

    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      const pageLimit = newPages.length === 0 ? availableHeightFirstPage : availableHeightSubsequent;

      if (currentHeight + sectionHeight > pageLimit) {
        newPages.push(currentPageSections);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
    });

    if (currentPageSections.length > 0) {
      newPages.push(currentPageSections);
    }
    setPages(newPages);
  }, [page.height, topBottomMargin, workExperiences, educationEntries, savedSkills, savedSummary, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, savedSoftware, savedVolunteering, savedInterests, savedWebsites, customSections, sectionOrder]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      splitContentIntoPages();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [splitContentIntoPages]);

  // Header component - shared between measurement and display
  const ResumeHeader = () => (
    <Box
      className="resume-header"
      sx={{
        bgcolor: "#fff",
        color: "#000",
        p: 0,
        mb: 3,
        ...globalFontStyle
      }}
    >
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={2} sx={{ minWidth: "auto", flexShrink: 0 }}>
          <Avatar
            src={!imageLoadError && photoUrl ? photoUrl : ""}
            onError={() => setImageLoadError(true)}
            sx={{ 
              width: 90, 
              height: 90, 
              border: `2px solid ${themeColor}`,
              bgcolor: themeColor,
              fontSize: "2rem",
              fontWeight: "bold",
              color: "#fff",
              flexShrink: 0
            }}
          >
            {data.firstName?.charAt(0)?.toUpperCase()}{data.lastName?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Grid>
        <Grid item xs={10} sx={{ minWidth: 0, overflow: "hidden" }}>
          <Typography variant="h4" sx={{ 
            fontWeight: "900", 
            color: "#222", 
            mb: 0.5,
            fontFamily: font,
            fontSize: "1.8rem",
            textTransform: "uppercase",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}>
            {data.firstName.toUpperCase()} {data.lastName.toUpperCase()}
          </Typography>
          <Typography variant="subtitle1" sx={{ 
            color: "gray", 
            fontWeight: 500, 
            mb: 1, 
            letterSpacing: 1,
            fontFamily: font,
            fontSize: "1rem",
            textTransform: "uppercase",
            wordBreak: "break-word",
          }}>
            {data.currentPosition}
          </Typography>
          <Box sx={{ 
            display: "flex", 
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}>
            {data.phone && (
              <Typography sx={{ ...getTextStyle(), display: "flex", alignItems: "center", fontSize: "12px" }}>
                <Phone sx={{ fontSize: 13, mr: 0.5, color: themeColor }} /> {data.phone}
              </Typography>
            )}
            {data.email && (
              <Typography sx={{ ...getTextStyle(), display: "flex", alignItems: "center", fontSize: "12px", wordBreak: "break-all" }}>
                <Email sx={{ fontSize: 13, mr: 0.5, color: themeColor, flexShrink: 0 }} /> {data.email}
              </Typography>
            )}
            {data.city && (
              <Typography sx={{ ...getTextStyle(), display: "flex", alignItems: "center", fontSize: "12px" }}>
                <LocationOn sx={{ fontSize: 13, mr: 0.5, color: themeColor }} /> {data.city}
              </Typography>
            )}
            {data.linkedIn && (
              <Typography sx={{ ...getTextStyle(), display: "flex", alignItems: "center", fontSize: "12px", wordBreak: "break-all" }}>
                <LinkedInIcon sx={{ fontSize: 13, mr: 0.5, color: themeColor, flexShrink: 0 }} /> {data.linkedIn}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderContentForMeasurement = () => (
    <Box ref={resumeRef} sx={{ 
      visibility: 'hidden', 
      position: 'absolute', 
      top: -9999,
      left: 0,
      width: getPageWidth(),
      overflow: "hidden",
      ...globalFontStyle 
    }}>
      <Box sx={{ 
        width: "100%", 
        p: `${topBottomMargin}px ${sideMargins}px`,
        boxSizing: "border-box",
        overflow: "hidden",
        ...globalFontStyle 
      }}>
        <ResumeHeader />
        {sectionOrder.map(sectionId => (
          <React.Fragment key={sectionId}>
            {renderSectionContent(sectionId)}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {!exporting && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
          '@media print': { display: 'none' }
        }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage === Math.max(0, pages.length - 1)} color="primary">
            <ChevronRight />
          </IconButton>
          
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button 
                onClick={handleArrangeSectionsClick}
                variant="contained" 
                startIcon={<Reorder />}
                color="primary"
              >
                Arrange Sections
              </Button>
              
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">
                Reset Section Order
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {renderContentForMeasurement()}

      <Box id="resume-container">
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                width: getPageWidth(),
                minHeight: getPageHeight(),
                mx: "auto",
                boxShadow: index === currentPage ? 3 : 0,
                bgcolor: "#fff",
                color: "#000",
                fontFamily: font,
                fontSize,
                fontStyle,
                lineHeight: `${lineSpacing}px`,
                p: `${topBottomMargin}px ${sideMargins}px`,
                pageBreakAfter: "always",
                boxSizing: "border-box",
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                display: exporting ? 'block' : (index === currentPage ? 'block' : 'none'),
                '@media print': { display: 'block' },
              }}
            >
              {index === 0 && <ResumeHeader />}
              <Box
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  overflow: "hidden",
                  width: "100%",
                  ...globalFontStyle
                }}
                dangerouslySetInnerHTML={{ 
                  __html: addFontToHtmlContent(pageContent.join('')) 
                }} 
              />
            </Box>
          ))
        ) : (
          <Box
            className="resume-page"
            sx={{
              width: getPageWidth(),
              minHeight: getPageHeight(),
              mx: "auto",
              boxShadow: 3,
              bgcolor: "#fff",
              color: "#000",
              fontFamily: font,
              fontSize,
              fontStyle,
              lineHeight: `${lineSpacing}px`,
              p: `${topBottomMargin}px ${sideMargins}px`,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Typography sx={{ p: 2, ...globalFontStyle }}>Loading resume...</Typography>
          </Box>
        )}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog
        open={arrangeDialogOpen}
        onClose={handleArrangeDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop to reorder sections. Sections will appear in the order shown below.
          </Typography>
          {renderDragPanel()}
        </DialogContent>

        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">
            Reset Order
          </Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume34;